import React, { useRef, useState } from "react";
import * as Icons from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader, SectionCard, Mono, InfoNote, ProvenanceBadge, SignatureBadge } from "@/components/uikit";
import { IdentityQR } from "@/components/IdentityQR";
import { verifyRecord, isValidReceipt } from "@/lib/crypto";
import { verifySignature } from "@/lib/signing";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";

export default function Verify() {
  const { records } = useApp();
  const [text, setText] = useState("");
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const runVerify = async () => {
    let record;
    try {
      record = JSON.parse(text);
    } catch {
      setResult({ error: "Invalid JSON — could not parse the receipt. Check the pasted text and try again." });
      return;
    }
    if (!record || typeof record !== "object" || Array.isArray(record)) {
      setResult({ error: "This does not look like a single SignalLink receipt (expected a JSON object)." });
      return;
    }
    if (typeof record.sha256 !== "string") {
      setResult({ error: "This does not look like a SignalLink receipt (missing sha256 field)." });
      return;
    }
    if (!isValidReceipt(record)) {
      setResult({
        error:
          "Receipt is structurally invalid or corrupted — it must include a 64-character sha256, an event_id, a timestamp, and a payload.",
      });
      return;
    }
    const res = await verifyRecord(record);
    const sig = await verifySignature(record.signature, res.computed);
    // Cross-check against local chain if present.
    const inChain = records.find((r) => r.event_id === record.event_id);
    setResult({ ...res, sig: sig.state, record, inChain: !!inChain, matchesChain: inChain ? inChain.sha256 === record.sha256 : null });
  };

  const importFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setText(String(reader.result || ""));
      toast.success("Receipt loaded", { description: file.name });
    };
    reader.onerror = () => toast.error("Could not read file");
    reader.readAsText(file);
    e.target.value = "";
  };

  const loadFromChain = () => {
    if (!records.length) {
      toast.error("No records in vault");
      return;
    }
    setText(JSON.stringify(records[records.length - 1], null, 2));
  };

  return (
    <div>
      <PageHeader overline="Integrity Assurance" title="Verify & Tamper Check" icon="ShieldCheck"
        subtitle="Paste JSON or import a SignalLink receipt. The engine recomputes the SHA-256 hash locally and reports integrity." />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <SectionCard title="Receipt Input" icon="ClipboardPaste" testId="verify-input-card">
          <div className="space-y-3">
            <Textarea data-testid="verify-json-input" rows={12} value={text} onChange={(e) => setText(e.target.value)}
              placeholder="Paste a SignalLink JSON receipt here…"
              className="border-slate-700 bg-slate-950 font-mono text-xs text-cyan-200" />
            <div className="flex flex-wrap gap-2">
              <Button onClick={runVerify} className="bg-cyan-500 font-bold text-slate-950 hover:bg-cyan-400" data-testid="verify-run-button">
                <Icons.ShieldCheck className="mr-2 h-4 w-4" /> Verify Integrity
              </Button>
              <Button variant="outline" className="border-slate-700 text-slate-200" onClick={() => fileRef.current?.click()} data-testid="verify-import-button">
                <Icons.Upload className="mr-2 h-4 w-4" /> Import JSON
              </Button>
              <Button variant="outline" className="border-slate-700 text-slate-200" onClick={loadFromChain} data-testid="verify-loadchain-button">
                <Icons.Database className="mr-2 h-4 w-4" /> Load latest anchor
              </Button>
              <input ref={fileRef} type="file" accept="application/json,.json" className="hidden" onChange={importFile} data-testid="verify-file-input" />
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Verification Result" icon="Fingerprint" testId="verify-result-card">
          {!result ? (
            <div className="flex min-h-[260px] flex-col items-center justify-center text-center">
              <Icons.Fingerprint className="mb-3 h-10 w-10 text-slate-700" aria-hidden="true" />
              <p className="text-sm text-slate-500">Verification output will appear here.</p>
            </div>
          ) : result.error ? (
            <InfoNote tone="danger" data-testid="verify-error">{result.error}</InfoNote>
          ) : (
            <div className="space-y-3" data-testid="verify-output">
              {result.ok ? (
                <div className="flex items-center gap-3 rounded-md border border-emerald-600 bg-emerald-950/50 p-3" data-testid="verify-status-ok">
                  <Icons.ShieldCheck className="h-7 w-7 shrink-0 text-emerald-400" aria-hidden="true" />
                  <div>
                    <p className="font-heading text-sm font-bold uppercase tracking-wide text-emerald-300">Local Cryptographic Integrity Verified</p>
                    <p className="text-xs text-emerald-200/80">Recomputed hash matches the receipt.</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3 rounded-md border border-rose-500 bg-rose-950/50 p-3" data-testid="verify-status-fail">
                  <Icons.ShieldAlert className="h-7 w-7 shrink-0 text-rose-400" aria-hidden="true" />
                  <div>
                    <p className="font-heading text-sm font-bold uppercase tracking-wide text-rose-300">Hash Mismatch — Tamper Detected</p>
                    <p className="text-xs text-rose-200/80">Recomputed hash differs from the stored receipt hash.</p>
                  </div>
                </div>
              )}
              <div className="flex flex-wrap items-center gap-2">
                <ProvenanceBadge source={result.record.source_tag || "local"} />
                <SignatureBadge state={result.sig} testId="verify-signature-badge" />
                <span className="rounded border border-slate-700 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-slate-400">
                  {(result.record.hash_mode || "canonical") === "raw" ? "RAW TEXT" : "CANONICAL JSON"}
                </span>
              </div>
              <Field label="event_id" value={result.record.event_id} />
              <Field label="receipt sha256" value={result.record.sha256} />
              <Field label="recomputed sha256" value={result.computed} tone={result.ok ? "ok" : "bad"} />
              {result.record.signature && (
                <div className="flex justify-center pt-1">
                  <IdentityQR
                    signer={{
                      public_key_id: result.record.signature.public_key_id,
                      algorithm: result.record.signature.algorithm,
                      public_jwk: result.record.signature.public_jwk,
                    }}
                    testId="verify-signer-qr"
                  />
                </div>
              )}
              {result.inChain && (
                <InfoNote tone={result.matchesChain ? "ok" : "warn"}>
                  {result.matchesChain
                    ? "This event exists in the local vault and its hash matches the stored chain record."
                    : "An event with this ID exists in the vault but its hash differs — the pasted receipt may have been altered."}
                </InfoNote>
              )}
              <InfoNote tone="info">
                Verification method: local SHA-256 recomputation (WebCrypto). This is <strong>not</strong> blockchain,
                RFC 3161, government, or third-party verification.
              </InfoNote>
            </div>
          )}
        </SectionCard>
      </div>
    </div>
  );
}

function Field({ label, value, tone }) {
  const toneClass = tone === "ok" ? "text-emerald-300" : tone === "bad" ? "text-rose-300" : "text-cyan-300";
  return (
    <div className="rounded border border-slate-800 bg-slate-950/60 px-3 py-1.5">
      <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
      <p className={`break-all font-mono text-xs ${toneClass}`}>{value}</p>
    </div>
  );
}
